import {
  Box,
  Button,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
} from "@mui/material";
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Assignment,
  Warning,
  Pending
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { 
  fetchStudentProfile, 
  submitStudentAdmission, 
  fetchMySubmissions, 
  updateStudentAdmission 
} from "../../../redux/Actions/studentActions";
import Meta from "../../Meta/Meta";
import StudentSubmissionSteps from "./StudentSubmissionSteps";
import StudentSubmissionStepContent from "./StudentSubmissionStepContent";
import AusbildungSubmissionForm from "./AusbildungSubmissionForm";
import { StudentDashboardLayout } from "./StudentDashboard";

const MAX_UPLOAD_SIZE = 83.89 * 1024 * 1024;

const StudentSubmissions = () => {
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language === "ar";
  const [activeTopTab, setActiveTopTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState([0]);
  const category = "ausbildung";
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.student.profile);
  const { submissions, submissionsLoading } = useSelector((state) => state.student);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submissionError, setSubmissionError] = useState("");
  const [feedback, setFeedback] = useState({ open: false, severity: "success", message: "" });

  const getStatusChip = (status) => {
    const name = (typeof status === 'string' ? status : status?.name)?.toLowerCase() || 'pending';
    switch (name) {
      case 'approved': return <Chip label={t("studentDashboard.submissions.status.approved")} color="success" size="small" icon={<CheckCircleIcon />} sx={{ fontWeight: 700 }} />;
      case 'rejected': return <Chip label={t("studentDashboard.submissions.status.rejected")} color="error" size="small" icon={<ErrorIcon />} sx={{ fontWeight: 700 }} />;
      default: return <Chip label={t("studentDashboard.submissions.status.pending")} color="warning" size="small" icon={<Pending />} sx={{ fontWeight: 700 }} />;
    }
  };
  const editFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    nationality: "",
    currentCountry: "",
    passportNumber: "",
    city: "",
    previousQualification: "",
    institutionName: "",
    graduationYear: "",
    gpa: "",
    desiredProgram: "",
    semesterStart: "",
    programLevel: "",
    englishLevel: "",
    germanLevel: "",
    otherLanguages: "",
    cv: "",
    certifications: "",
    highSchoolDocument: "",
    passportDocument: "",
    transcriptDocument: "",
    motivationLetter: "",
    audioMessage: "",
    applicationDocuments: "",
    experienceCertificates: [],
    consentGiven: false,
  });

  const stepLabels = t("studentDashboard.submissions.stepLabels", {
    returnObjects: true,
  });
  useEffect(() => {
    const desiredAusbildung = searchParams.get("desiredAusbildung");

    if (desiredAusbildung) {
      setFormData((prev) => ({ ...prev, desiredProgram: desiredAusbildung }));
      setActiveStep(0);
      setVisitedSteps([0]);
      setActiveTopTab(0);
    }
  }, [searchParams]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setSubmissionError("");
  };

  const handleTopTabChange = (_, newValue) => {
    setActiveTopTab(newValue);
  };

  const handleStepChange = (_, newValue) => {
    setActiveStep(newValue);
    setVisitedSteps((prev) => (prev.includes(newValue) ? prev : [...prev, newValue]));
  };

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { roleName, roleNames } = user || {};
  
  // Normalize roles into a single list for checking
  const allRoles = useMemo(
    () =>
      (Array.isArray(roleNames) ? roleNames : [])
        .concat(roleName ? [roleName] : [])
        .map((r) => r.toUpperCase()),
    [roleName, roleNames]
  );

  useEffect(() => {
    if (isAuthenticated && allRoles.includes("STUDENT")) {
      if (!profile) {
        dispatch(fetchStudentProfile());
      }
      dispatch(fetchMySubmissions());
    }
  }, [dispatch, profile, isAuthenticated, allRoles]);

  const handleEditOpen = (submission) => {
    setSelectedSubmission(submission);
    setEditFormData({
      firstName: submission.firstName || "",
      lastName: submission.lastName || "",
      email: submission.email || "",
      phone: submission.phone || "",
      dob: submission.dob || "",
      nationality: submission.nationality || "",
      currentCountry: submission.currentCountry || "",
      passportNumber: submission.passportNumber || "",
      city: submission.city || "",
      previousQualification: submission.previousQualification || "",
      institutionName: submission.institutionName || "",
      graduationYear: submission.graduationYear || "",
      gpa: submission.gpa || "",
      desiredProgram: submission.desiredProgram || "",
      semesterStart: submission.semesterStart || "",
      programLevel: submission.programLevel || "",
      englishLevel: submission.englishLevel || "",
      germanLevel: submission.germanLevel || "",
      otherLanguages: submission.otherLanguages || "",
      category: submission.category || "university",
      // Files
      certifications: submission.certificationsFile || "",
      cv: submission.cvFile || "",
      highSchoolDocument: submission.highSchoolFile || "",
      passportDocument: submission.passportFile || "",
      transcriptDocument: submission.transcriptFile || "",
      motivationLetter: submission.motivationFile || "",
      audioMessage: submission.audioMessageFile || "",
      applicationDocuments: submission.applicationDocumentsFile || "",
      experienceCertificates: submission.experienceCertificatesFiles || [],
      consentGiven: Boolean(submission.consentGiven),
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (field) => (event) => {
    setEditFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditFileUpload = (type) => (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_UPLOAD_SIZE) {
        alert(t("studentDashboard.submissions.ausbildungForm.maximumFileSize"));
        event.target.value = "";
        return;
      }
      setEditFormData((prev) => ({ ...prev, [type]: file }));
    }
    if (event.target) event.target.value = "";
  };

  const handleEditDeleteFile = (type, index) => {
    if (Number.isInteger(index)) {
      setEditFormData((prev) => ({ ...prev, [type]: (prev[type] || []).filter((_, itemIndex) => itemIndex !== index) }));
      return;
    }
    setEditFormData((prev) => ({ ...prev, [type]: "" }));
  };

  const handleEditMultipleFileUpload = (type) => (event) => {
    const files = Array.from(event.target.files || []).filter((file) => file.size <= MAX_UPLOAD_SIZE);
    if (files.length) setEditFormData((prev) => ({ ...prev, [type]: [...(prev[type] || []), ...files] }));
    if (event.target) event.target.value = "";
  };

  useEffect(() => {
    if (!profile) return;

    const formattedDob = profile.dateOfBirth
      ? new Date(profile.dateOfBirth).toISOString().split("T")[0]
      : "";

    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || profile.firstName || "",
      lastName: prev.lastName || profile.lastName || "",
      email: prev.email || profile.email || "",
      phone: prev.phone || profile.phoneNumber || profile.phone || "",
      dob: prev.dob || formattedDob,
      nationality: prev.nationality || profile.nationality || "",
    }));
  }, [profile]);

  const handleFileUpload = (type) => (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (type === "applicationDocuments" && (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf"))) {
        alert("Please upload one PDF file.");
        if (event.target) event.target.value = "";
        return;
      }
      if (type === "applicationDocuments" && file.size > 15 * 1024 * 1024) {
        alert("The application PDF must not exceed 15 MB.");
        if (event.target) event.target.value = "";
        return;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        alert(t("studentDashboard.submissions.ausbildungForm.maximumFileSize"));
        event.target.value = "";
        return;
      }
      setFormData((prev) => ({ ...prev, [type]: file }));
      setValidationErrors((prev) => {
        if (!prev[type]) return prev;
        const next = { ...prev };
        delete next[type];
        return next;
      });
      setSubmissionError("");
    }
    // Reset input value to allow selecting same file again if deleted
    if (event.target) event.target.value = "";
  };

  const handleDeleteFile = (type) => {
    setFormData((prev) => ({ ...prev, [type]: "" }));
  };

  const handleMultipleFileUpload = (type) => (event) => {
    const files = Array.from(event.target.files || []).filter((file) => file.size <= MAX_UPLOAD_SIZE);
    if (files.length) setFormData((prev) => ({ ...prev, [type]: [...(prev[type] || []), ...files] }));
    if (event.target) event.target.value = "";
  };

  const handleDeleteAusbildungFile = (type, index) => {
    if (Number.isInteger(index)) {
      setFormData((prev) => ({ ...prev, [type]: (prev[type] || []).filter((_, itemIndex) => itemIndex !== index) }));
      return;
    }
    handleDeleteFile(type);
  };

  const fieldStep = {
    firstName: 0,
    lastName: 0,
    email: 0,
    phone: 0,
    nationality: 0,
    currentCountry: 0,
    desiredProgram: 0,
    germanLevel: 0,
    cv: 1,
    motivationLetter: 1,
    highSchoolDocument: 1,
    certifications: 1,
    audioMessage: 1,
    consentGiven: 2,
  };

  const fieldLabels = {
    firstName: t("studentDashboard.submissions.fields.firstName"),
    lastName: t("studentDashboard.submissions.fields.lastName"),
    email: t("studentDashboard.submissions.fields.email"),
    phone: t("studentDashboard.submissions.fields.phone"),
    nationality: t("studentDashboard.submissions.fields.nationality"),
    currentCountry: t("studentDashboard.submissions.ausbildungForm.currentCountry"),
    desiredProgram: t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" }),
    germanLevel: t("studentDashboard.submissions.fields.germanLevel"),
    cv: t("studentDashboard.submissions.ausbildungForm.cv"),
    motivationLetter: t("studentDashboard.submissions.fields.motivationLetter"),
    highSchoolDocument: t("studentDashboard.submissions.fields.schoolCertificate"),
    certifications: t("studentDashboard.submissions.ausbildungForm.germanB2Certificate"),
    audioMessage: t("studentDashboard.submissions.fields.audioMessage", { defaultValue: "Audio Message" }),
    consentGiven: t("studentDashboard.submissions.ausbildungForm.privacyLink"),
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      "firstName", "lastName", "email", "phone", "nationality", "currentCountry",
      "desiredProgram", "germanLevel", "cv", "motivationLetter", "highSchoolDocument", "certifications", "audioMessage",
    ];
    requiredFields.forEach((field) => {
      const value = formData[field];
      const missing = value instanceof File ? value.size === 0 : !String(value || "").trim();
      if (missing) {
        errors[field] = t("studentDashboard.submissions.validation.required", {
          field: fieldLabels[field],
          defaultValue: `${fieldLabels[field]} is required.`,
        });
      }
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t("studentDashboard.submissions.validation.email", { defaultValue: "Enter a valid email address." });
    }
    if (!formData.consentGiven) {
      errors.consentGiven = t("studentDashboard.submissions.validation.consent", { defaultValue: "Please accept the privacy consent before submitting." });
    }
    return errors;
  };

  const validateCurrentStep = () => {
    const errors = validateForm();
    const currentErrors = Object.fromEntries(Object.entries(errors).filter(([field]) => fieldStep[field] === activeStep));
    setValidationErrors((prev) => ({
      ...Object.fromEntries(Object.entries(prev).filter(([field]) => fieldStep[field] !== activeStep)),
      ...currentErrors,
    }));
    if (Object.keys(currentErrors).length > 0) {
      window.requestAnimationFrame(() => document.getElementById("ausbildung-form-errors")?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return false;
    }
    return true;
  };

  const getApiErrorMessage = (error) => {
    const payload = error?.response?.data;
    if (typeof payload === "string" && payload.trim()) return payload;
    if (payload?.message) return payload.message;
    if (payload?.error) return payload.error;
    return t("studentDashboard.submissions.validation.submitFailed", {
      defaultValue: "The application could not be submitted. Please review the fields and try again.",
    });
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (isSubmitting) return;

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmissionError("");
      const firstInvalidStep = Math.min(...Object.keys(errors).map((field) => fieldStep[field]));
      setActiveStep(firstInvalidStep);
      setVisitedSteps((prev) => Array.from(new Set([...prev, firstInvalidStep])));
      window.requestAnimationFrame(() => document.getElementById("ausbildung-form-errors")?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }

    setValidationErrors({});
    setSubmissionError("");
    setIsSubmitting(true);
    
    const data = new FormData();
    
    const dto = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      dob: formData.dob,
      nationality: formData.nationality,
      currentCountry: formData.currentCountry,
      passportNumber: formData.passportNumber,
      city: formData.city,
      previousQualification: formData.previousQualification,
      institutionName: formData.institutionName,
      graduationYear: formData.graduationYear,
      gpa: formData.gpa,
      desiredProgram: formData.desiredProgram,
      programLevel: formData.programLevel,
      semesterStart: formData.semesterStart,
      englishLevel: formData.englishLevel,
      germanLevel: formData.germanLevel,
      otherLanguages: formData.otherLanguages,
      category: category,
      consentGiven: formData.consentGiven
    };

    data.append("data", new Blob([JSON.stringify(dto)], { type: "application/json" }));
    
    if (formData.certifications instanceof File) data.append("certifications", formData.certifications);
    if (formData.highSchoolDocument instanceof File) data.append("highSchool", formData.highSchoolDocument);
    if (formData.passportDocument instanceof File) data.append("passport", formData.passportDocument);
    if (formData.transcriptDocument instanceof File) data.append("transcript", formData.transcriptDocument);
    if (formData.motivationLetter instanceof File) data.append("motivation", formData.motivationLetter);
    if (formData.audioMessage instanceof File) data.append("audioMessage", formData.audioMessage);
    if (formData.cv instanceof File) data.append("cv", formData.cv);
    (formData.experienceCertificates || []).filter((file) => file instanceof File).forEach((file) => data.append("experienceCertificates", file));

    try {
      await dispatch(submitStudentAdmission(data));
      setFeedback({
        open: true,
        severity: "success",
        message: t("studentDashboard.submissions.feedback.submitSuccess"),
      });
      setActiveTopTab(1);
      dispatch(fetchMySubmissions());
    } catch (err) {
      console.error("Submission error:", err);
      setSubmissionError(getApiErrorMessage(err));
      setFeedback({
        open: true,
        severity: "error",
        message: t("studentDashboard.submissions.feedback.submitError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    setIsSubmittingEdit(true);
    const data = new FormData();
    
    const dto = { ...editFormData };
    // Remove file objects from DTO
    delete dto.certifications;
    delete dto.highSchoolDocument;
    delete dto.passportDocument;
    delete dto.transcriptDocument;
    delete dto.motivationLetter;
    delete dto.audioMessage;
    delete dto.applicationDocuments;
    delete dto.cv;
    delete dto.experienceCertificates;

    data.append("data", new Blob([JSON.stringify(dto)], { type: "application/json" }));
    
    if (editFormData.certifications instanceof File) data.append("certifications", editFormData.certifications);
    if (editFormData.highSchoolDocument instanceof File) data.append("highSchool", editFormData.highSchoolDocument);
    if (editFormData.passportDocument instanceof File) data.append("passport", editFormData.passportDocument);
    if (editFormData.transcriptDocument instanceof File) data.append("transcript", editFormData.transcriptDocument);
    if (editFormData.motivationLetter instanceof File) data.append("motivation", editFormData.motivationLetter);
    if (editFormData.audioMessage instanceof File) data.append("audioMessage", editFormData.audioMessage);
    if (editFormData.cv instanceof File) data.append("cv", editFormData.cv);
    (editFormData.experienceCertificates || []).filter((file) => file instanceof File).forEach((file) => data.append("experienceCertificates", file));

    try {
      await dispatch(updateStudentAdmission(selectedSubmission.id, data));
      setIsEditModalOpen(false);
      setFeedback({
        open: true,
        severity: "success",
        message: t("studentDashboard.submissions.feedback.updateSuccess"),
      });
    } catch (err) {
      console.error("Update error:", err);
      setFeedback({
        open: true,
        severity: "error",
        message: t("studentDashboard.submissions.feedback.updateError"),
      });
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const getMissingFields = (sub) => {
    if (!sub) return [];
    const universityRequired = [
      { key: 'firstName', label: t("studentDashboard.submissions.fields.firstName") },
      { key: 'lastName', label: t("studentDashboard.submissions.fields.lastName") },
      { key: 'email', label: t("studentDashboard.submissions.fields.email") },
      { key: 'phone', label: t("studentDashboard.submissions.fields.phone") },
      { key: 'dob', label: t("studentDashboard.submissions.fields.dob") },
      { key: 'nationality', label: t("studentDashboard.submissions.fields.nationality") },
      { key: 'passportNumber', label: t("studentDashboard.submissions.fields.passportNumber") },
      { key: 'city', label: t("studentDashboard.submissions.fields.city") },
      { key: 'previousQualification', label: t("studentDashboard.submissions.fields.previousQualification") },
      { key: 'institutionName', label: t("studentDashboard.submissions.fields.institutionName") },
      { key: 'graduationYear', label: t("studentDashboard.submissions.fields.graduationYear") },
      { key: 'gpa', label: t("studentDashboard.submissions.fields.gpa") },
      { key: 'desiredProgram', label: t("studentDashboard.submissions.fields.desiredProgram") },
      { key: 'programLevel', label: t("studentDashboard.submissions.fields.programLevel") },
      { key: 'certificationsFile', label: t("studentDashboard.submissions.fields.certifications") },
      { key: 'highSchoolFile', label: t("studentDashboard.submissions.fields.highSchoolDocument") },
      { key: 'passportFile', label: t("studentDashboard.submissions.fields.passportDocument") },
      { key: 'transcriptFile', label: t("studentDashboard.submissions.fields.transcriptDocument") },
      { key: 'motivationFile', label: t("studentDashboard.submissions.fields.motivationLetter") },
    ];
    const ausbildungRequired = [
      { key: 'firstName', label: t("studentDashboard.submissions.fields.firstName") },
      { key: 'lastName', label: t("studentDashboard.submissions.fields.lastName") },
      { key: 'email', label: t("studentDashboard.submissions.fields.email") },
      { key: 'desiredProgram', label: t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" }) },
      { key: 'germanLevel', label: t("studentDashboard.submissions.fields.germanLevel") },
      { key: 'cvFile', label: t("studentDashboard.submissions.ausbildungForm.cv") },
      { key: 'certificationsFile', label: t("studentDashboard.submissions.fields.languageCertificate", { defaultValue: "Language Certificate" }) },
      { key: 'highSchoolFile', label: t("studentDashboard.submissions.fields.schoolCertificate", { defaultValue: "School Certificate" }) },
      { key: 'motivationFile', label: t("studentDashboard.submissions.fields.motivationLetter") },
      { key: 'audioMessageFile', label: t("studentDashboard.submissions.fields.audioMessage", { defaultValue: "Audio Message" }) },
    ];
    const required = sub.category === "ausbildung" ? ausbildungRequired : universityRequired;
    return required.filter(f => !sub[f.key]).map(f => f.label);
  };

  const renderStatusTab = () => {
    if (submissionsLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>;
    
    if (!submissions || submissions.length === 0) {
      return (
        <Box sx={{ py: 8, textAlign: "center", bgcolor: "#f9fafb", borderRadius: 4, border: "2px dashed #e5e7eb" }}>
          <Box sx={{ bgcolor: "#fff", w: 64, h: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2, boxShadow: "0 4px 10px rgba(0,0,0,0.05)" }}>
            <Assignment sx={{ color: "#9ca3af", fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} gutterBottom>{t("studentDashboard.submissions.status.noSubmissionTitle")}</Typography>
          <Typography variant="body2" color="text.secondary">{t("studentDashboard.submissions.status.noSubmissionMessage")}</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {submissions.map((sub) => {
          const missing = getMissingFields(sub);
          return (
            <Grid item xs={12} key={sub.id}>
              <Card sx={{ borderRadius: 4, border: "1px solid #e5e7eb", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", overflow: "visible" }}>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                        <Typography variant="h6" fontWeight={800} color="#2D3E50">
                          {sub.category === "university" ? t("studentDashboard.submissions.categories.university") : t("studentDashboard.submissions.categories.ausbildung")}
                        </Typography>
                        {getStatusChip(sub.status)}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>
                          {sub.category === "ausbildung"
                            ? t("studentDashboard.submissions.fields.desiredAusbildung", { defaultValue: "Desired Ausbildung" })
                            : t("studentDashboard.submissions.fields.desiredProgram")}
                          :
                        </strong> {sub.desiredProgram}
                      </Typography>
                      {sub.category === "ausbildung" ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>{t("studentDashboard.submissions.fields.germanLevel")}:</strong> {sub.germanLevel || t("studentDashboard.submissions.notProvided")}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                           <strong>{t("studentDashboard.submissions.fields.semesterStart")}:</strong> {sub.semesterStart}
                        </Typography>
                      )}
                      
                      {missing.length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: "#fff9f0", borderRadius: 3, border: "1px solid #ffecb3" }}>
                          <Typography variant="caption" fontWeight={800} color="#b45309" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                            <Warning sx={{ fontSize: 14 }} /> {t("studentDashboard.submissions.status.missingFieldsTitle")}
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {missing.map((f, i) => (
                              <Chip key={i} label={f} size="small" variant="outlined" sx={{ bgcolor: "#fff", height: 20, fontSize: "0.65rem", fontWeight: 700, color: "#b45309", borderColor: "#ffcc80" }} />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Documents Section */}
                      <Box sx={{ mt: 2.5 }}>
                        <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: "block", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {t("studentDashboard.submissions.status.documentsTitle")}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {(sub.category === "ausbildung" ? [
                            { key: 'cvFile', label: t("studentDashboard.submissions.ausbildungForm.cv") },
                            { key: 'generatedCvFile', label: "Generated ALGermanist CV" },
                            { key: 'certificationsFile', label: t("studentDashboard.submissions.ausbildungForm.germanB2Certificate") },
                            { key: 'highSchoolFile', label: t("studentDashboard.submissions.fields.schoolCertificate", { defaultValue: "School Certificate" }) },
                            { key: 'motivationFile', label: t("studentDashboard.submissions.fields.motivationLetter") },
                            { key: 'audioMessageFile', label: t("studentDashboard.submissions.fields.audioMessage", { defaultValue: "Audio Message" }) }
                          ] : [
                            { key: 'certificationsFile', label: t("studentDashboard.submissions.fields.certifications") },
                            { key: 'highSchoolFile', label: t("studentDashboard.submissions.fields.highSchoolDocument") },
                            { key: 'passportFile', label: t("studentDashboard.submissions.fields.passportDocument") },
                            { key: 'transcriptFile', label: t("studentDashboard.submissions.fields.transcriptDocument") },
                            { key: 'motivationFile', label: t("studentDashboard.submissions.fields.motivationLetter") }
                          ]).map((doc) => (
                            sub[doc.key] && (
                              <Button
                                key={doc.key}
                                size="small"
                                variant="outlined"
                                href={`${process.env.REACT_APP_API_URL}/api/student-admissions/download/${sub[doc.key]}`}
                                target="_blank"
                                sx={{ 
                                  borderRadius: 2, 
                                  textTransform: "none", 
                                  fontSize: "0.7rem", 
                                  fontWeight: 700,
                                  borderColor: "#e5e7eb",
                                  color: "#2D3E50",
                                  '&:hover': { bgcolor: "#f9fafb", borderColor: "#2D3E50" }
                                }}
                              >
                                {t("studentDashboard.submissions.status.viewDocument", { document: doc.label })}
                              </Button>
                            )
                          ))}
                          {sub.category === "ausbildung" && (sub.experienceCertificatesFiles || []).map((path, index) => (
                            <Button key={path} size="small" variant="outlined" href={`${process.env.REACT_APP_API_URL}/api/student-admissions/download/${path}`} target="_blank" sx={{ borderRadius: 2, textTransform: "none", fontSize: "0.7rem", fontWeight: 700 }}>
                              {t("studentDashboard.submissions.ausbildungForm.experienceCertificates")} {index + 1}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: { md: "right" } }}>
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditOpen(sub)}
                        sx={{ 
                          borderRadius: 3, 
                          textTransform: "none", 
                          fontWeight: 700,
                          bgcolor: "#2D3E50",
                          '&:hover': { bgcolor: "#1a252f" }
                        }}
                      >
                        {t("studentDashboard.submissions.status.editAction")}
                      </Button>
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        {t("studentDashboard.submissions.status.created")}: {new Date(sub.createdAt).toLocaleDateString(i18n.language)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderFormStep = () => (
    <AusbildungSubmissionForm
      activeStep={activeStep}
      formData={formData}
      handleChange={handleChange}
      handleFileUpload={handleFileUpload}
      handleMultipleFileUpload={handleMultipleFileUpload}
      handleDeleteFile={handleDeleteAusbildungFile}
      handleSubmit={handleSubmit}
      isArabic={isArabic}
      t={t}
      lockDesiredProgram={Boolean(searchParams.get("desiredAusbildung"))}
      validationErrors={validationErrors}
      isSubmitting={isSubmitting}
    />
  );

  return (
    <StudentDashboardLayout>
      <Box
        sx={{ py: { xs: 2, sm: 4 }, my: { xs: 2, md: 8 }, px: { xs: 1, sm: 4 }, bgcolor: "#F6F8F8", overflowX: "hidden" }}
        dir={isArabic ? "rtl" : "ltr"}
      >
        <Meta
          title="ALGermanist - Application Submissions"
          description="Submit your Ausbildung application and track its status from the student dashboard."
          keywords="student dashboard, application submissions, Ausbildung application"
        />

        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mb: 1, textAlign: isArabic ? "right" : "left" }}
            >
              {t("studentDashboard.submissions.pageTitle")}
            </Typography>
            <Typography sx={{ color: "text.secondary", textAlign: isArabic ? "right" : "left" }}>
              {t("studentDashboard.submissions.pageSubtitle")}
            </Typography>
          </Box>
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 4,
            "& .MuiFormControl-root": {
              direction: isArabic ? "rtl" : "ltr",
            },
            "& .MuiInputLabel-root": {
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
              width: isArabic ? "auto" : "100%",
              maxWidth: isArabic ? "calc(100% - 32px)" : undefined,
              left: isArabic ? "auto" : undefined,
              right: isArabic ? 14 : undefined,
              fontWeight: isArabic ? 500 : undefined,
              transform: isArabic ? "translate(0, 16px) scale(1)" : undefined,
            },
            "& .MuiInputLabel-root.MuiInputLabel-shrink, & .MuiInputLabel-root.Mui-focused": {
              transformOrigin: isArabic ? "right top" : "left top",
              transform: isArabic ? "translate(0, -9px) scale(0.85)" : undefined,
            },
            "& .MuiOutlinedInput-root": {
              direction: isArabic ? "rtl" : "ltr",
            },
            "& .MuiInputBase-input, & input, & textarea": {
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            },
            "& input::placeholder, & textarea::placeholder, & input::-webkit-input-placeholder, & input::-moz-placeholder, & input:-ms-input-placeholder, & input::-ms-input-placeholder": {
              textAlign: isArabic ? "right" : "left",
            },
            "& .MuiSelect-select": {
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
              paddingInlineStart: 20,
              paddingInlineEnd: 20,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
            },
            "& .MuiOutlinedInput-root .MuiSelect-icon": {
              right: isArabic ? "auto" : 12,
              left: isArabic ? 12 : "auto",
            },
          }}
        >
          <Tabs
            value={activeTopTab}
            onChange={handleTopTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{ mb: 4 }}
          >
            <Tab label={t("studentDashboard.submissions.tabs.form")} />
            <Tab label={t("studentDashboard.submissions.tabs.status")} />
          </Tabs>

          {activeTopTab === 0 && (
            <Box>
              <>
                  {(Object.keys(validationErrors).length > 0 || submissionError) && (
                    <Alert id="ausbildung-form-errors" severity="error" sx={{ mb: 3 }} role="alert">
                      <AlertTitle>
                        {submissionError
                          ? t("studentDashboard.submissions.validation.serverTitle", { defaultValue: "Submission failed" })
                          : t("studentDashboard.submissions.validation.missingTitle", { defaultValue: "Please complete the highlighted fields" })}
                      </AlertTitle>
                      {submissionError || t("studentDashboard.submissions.validation.missingHint", { defaultValue: "Open the highlighted step and complete the following information:" })}
                      {Object.keys(validationErrors).length > 0 && (
                        <Box component="ul" sx={{ my: 1, ps: 3 }}>
                          {Object.entries(validationErrors).map(([field, message]) => <li key={field}>{message}</li>)}
                        </Box>
                      )}
                    </Alert>
                  )}
                  <StudentSubmissionSteps
                    activeStep={activeStep}
                    onChange={handleStepChange}
                    stepLabels={stepLabels}
                    isArabic={isArabic}
                    visitedSteps={visitedSteps}
                  />

                  {renderFormStep()}

                  <Box
                    sx={{
                      mt: 4,
                      display: "flex",
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      justifyContent: isArabic ? "flex-start" : "flex-end",
                      gap: 2,
                    }}
                  >
                    {activeStep > 0 && (
                      <Button
                        variant="outlined"
                        disabled={isSubmitting}
                        onClick={() => {
                          const nextStep = activeStep - 1;
                          setActiveStep(nextStep);
                          setVisitedSteps((prev) => (prev.includes(nextStep) ? prev : [...prev, nextStep]));
                        }}
                        sx={{ textTransform: "none", borderRadius: 3, width: { xs: "100%", sm: "auto" } }}
                      >
                        {t("studentDashboard.submissions.stepActions.previous")}
                      </Button>
                    )}
                    {activeStep < 2 ? (
                      <Button
                        variant="contained"
                        disabled={isSubmitting}
                        onClick={() => {
                          if (!validateCurrentStep()) return;
                          const nextStep = activeStep + 1;
                          setActiveStep(nextStep);
                          setVisitedSteps((prev) => (prev.includes(nextStep) ? prev : [...prev, nextStep]));
                        }}
                        sx={{ textTransform: "none", borderRadius: 3, width: { xs: "100%", sm: "auto" } }}
                      >
                        {t("studentDashboard.submissions.stepActions.next")}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{ textTransform: "none", borderRadius: 3, minWidth: 180, width: { xs: "100%", sm: "auto" } }}
                      >
                        {isSubmitting ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            {t("studentDashboard.submissions.validation.submitting", { defaultValue: "Submitting..." })}
                          </Box>
                        ) : t("studentDashboard.submissions.stepActions.submit")}
                      </Button>
                    )}
                  </Box>
                </>
            </Box>
          )}

          {activeTopTab === 1 && renderStatusTab()}
        </Paper>
      </Box>

      {/* Edit Modal */}
      <Dialog 
        open={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        maxWidth="md" 
        fullWidth
        dir={isArabic ? "rtl" : "ltr"}
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 4 },
            direction: isArabic ? "rtl" : "ltr",
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
            m: { xs: 1, sm: 4 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, p: 3 }}>
          {t("studentDashboard.submissions.status.editTitle")}
          <Typography variant="body2" color="text.secondary">
            {t("studentDashboard.submissions.status.editDescription")}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            overflowX: "hidden",
            "& .MuiFormControl-root": {
              direction: isArabic ? "rtl" : "ltr",
            },
            "& .MuiInputLabel-root": {
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
              width: isArabic ? "auto" : "100%",
              maxWidth: isArabic ? "calc(100% - 32px)" : undefined,
              left: isArabic ? "auto" : undefined,
              right: isArabic ? 14 : undefined,
              fontWeight: isArabic ? 500 : undefined,
              transform: isArabic ? "translate(0, 16px) scale(1)" : undefined,
            },
            "& .MuiInputLabel-root.MuiInputLabel-shrink, & .MuiInputLabel-root.Mui-focused": {
              transformOrigin: isArabic ? "right top" : "left top",
              transform: isArabic ? "translate(0, -9px) scale(0.85)" : undefined,
            },
            "& .MuiInputBase-input, & input, & textarea": {
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            },
          }}
        >
           {editFormData.category === "ausbildung" ? (
             <AusbildungSubmissionForm
                activeStep={-1}
                formData={editFormData}
                handleChange={handleEditChange}
                handleFileUpload={handleEditFileUpload}
                handleMultipleFileUpload={handleEditMultipleFileUpload}
                handleDeleteFile={handleEditDeleteFile}
                handleSubmit={handleEditSubmit}
                isArabic={isArabic}
                t={t}
                isEdit={true}
                lockDesiredProgram={false}
              />
           ) : (
             <StudentSubmissionStepContent
                activeStep={-1} // Special case for "All Fields" or just reuse content
                formData={editFormData}
                handleChange={handleEditChange}
                handleFileUpload={handleEditFileUpload}
                handleDeleteFile={handleEditDeleteFile}
                fileInputRef={editFileInputRef}
                handleSubmit={handleEditSubmit}
                isArabic={isArabic}
                t={t}
                isEdit={true}
              />
           )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 3 }, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setIsEditModalOpen(false)} sx={{ textTransform: "none" }}>
            {t("studentDashboard.submissions.status.cancel")}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit} 
            disabled={isSubmittingEdit}
            sx={{ textTransform: "none", borderRadius: 2, px: 4 }}
          >
            {isSubmittingEdit ? <CircularProgress size={24} color="inherit" /> : t("studentDashboard.submissions.status.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setFeedback((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: isArabic ? "left" : "right" }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </StudentDashboardLayout>
  );
};

export default StudentSubmissions;
